import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import "./PickupQuickTimeSelector.scss";

export type PickupQuickTimeSelectorOption = {
    key: string;
    label: string;
    value: string;
    apiValue?: string;
    displayValue?: string;
    date?: string;
    time?: string;
};

type PickupQuickTimeSelectorProps = {
    options?: PickupQuickTimeSelectorOption[];
    selectedValue?: string | number;
    onSelect?: (option: PickupQuickTimeSelectorOption) => void;
    disabled?: boolean;
    baseValueText?: ReactNode;
    finalValueText?: ReactNode;
    finalDropoffValueText?: ReactNode;
    labelsPosition?: "top" | "bottom";
    compact?: boolean;
    optionsClassName?: string;
};

const PickupQuickTimeSelector = ({
    options = [],
    selectedValue = "",
    onSelect,
    disabled = false,
    baseValueText = "",
    finalValueText = "",
    finalDropoffValueText = "",
    labelsPosition = "top",
    compact = false,
    optionsClassName = "mt-4",
}: PickupQuickTimeSelectorProps) => {
    const { t } = useTranslation();

    const renderDropOffPreview = ({ className } : { className: string }) => !finalDropoffValueText ? null : (
        <div className={`pickup-quick-time-row border-top pt-3 ${className}`}>
            <span className="pickup-quick-time-label">{t("dropOffTime")}:</span>
            <span className="pickup-quick-time-value font-monospace">{finalDropoffValueText || "- - -"}</span>
        </div>
    );

    const renderLabels = () => (
        <div className={`pickup-quick-time-main ${labelsPosition === "bottom" ? "mt-3" : ""}`.trim()}>
            <div className="pickup-quick-time-row opacity-50">
                <span className="pickup-quick-time-label">{t("pickupTimeBaseLabel")}:</span>
                <span className="pickup-quick-time-value font-monospace">{baseValueText || "- - -"}</span>
            </div>
            <div className="pickup-quick-time-row">
                <span className="pickup-quick-time-label">{t("pickupTimeFinalLabel")}:</span>
                <span className="pickup-quick-time-value font-monospace">{finalValueText || "- - -"}</span>
            </div>
            {labelsPosition === "bottom" && renderDropOffPreview({ className: "mt-2" })}
        </div>
    );

    return (
        <div className={`pickup-quick-time-box ${compact ? "pickup-quick-time-box-compact" : ""}`.trim()}>
            {labelsPosition === "top" && renderLabels()}

            <div className={`pickup-quick-time-options ${optionsClassName}`.trim()}>
                {options.map((option) => {
                    const optionValue = `${option?.value ?? option?.key ?? ""}`;
                    const isSelected = optionValue === `${selectedValue || ""}`;

                    return (
                        <button
                            key={option?.key || optionValue}
                            type="button"
                            className={`btn pickup-quick-time-btn ${isSelected ? "btn-dark" : "btn-outline-dark"}`}
                            disabled={disabled}
                            onClick={() => onSelect?.(option)}
                        >
                            {option?.label || "-"}
                        </button>
                    );
                })}
            </div>

            {labelsPosition === "bottom" && renderLabels()}

            {labelsPosition === "top" && renderDropOffPreview({ className: "mt-4" })}
        </div>
    );
};

export default PickupQuickTimeSelector;
